/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { formatLocalizedDate } from '@/shared/utils/date';
import { formatDealMoney } from '../utils/deal-state';
import type { DealProposal } from '@/services/requests/housien-services/DealsService';

/**
 * Whether the offer is accepted but still waiting on the world.
 */
export function isConditionallyAccepted(proposal: DealProposal): boolean {
  return proposal.status === 'accepted' && hasPendingConditions(proposal);
}

/**
 * Whether anything on the offer is still unanswered.
 */
export function hasPendingConditions(proposal: DealProposal): boolean {
  return (proposal.sale_terms?.conditions ?? []).some((c) => c.state === 'pending');
}

/**
 * Whether the offer's own deadline has passed.
 *
 * Informational only, exactly as on the backend: an expired offer keeps
 * its status and still holds the attendance where it is. A date passing
 * is a note the broker left themselves, not a decision anybody made.
 */
export function isExpired(proposal: DealProposal): boolean {
  if (!proposal.valid_until) return false;
  const deadline = new Date(proposal.valid_until);
  return !Number.isNaN(deadline.getTime()) && deadline < new Date();
}

const CONDITION_CLASS: Record<string, string> = {
  pending: 'border-(--border-medium) text-(--text-secondary)',
  met: 'border-(--accent-salvia)/40 text-(--accent-salvia)',
  failed: 'border-(--accent-cobre)/40 text-(--accent-cobre)',
};

/**
 * One line of the offer's terms: a label and what it says.
 */
function TermLine({ label, value }: { label: string; value: string }) {
  return (
    <Text component="span" className="text-[length:var(--text-meta)] text-(--text-secondary)">
      <Text component="span" className="text-(--text-tertiary)">{`${label}: `}</Text>
      {value}
    </Text>
  );
}

/**
 * What a rental offer says beyond its number.
 */
function RentalSummary({ proposal }: { proposal: DealProposal }) {
  const { t } = useTranslation('features/deals');
  const terms = proposal.rental_terms;
  if (!terms) return null;
  const guarantee = terms.guarantee;

  return (
    <>
      {guarantee?.kind && (
        <TermLine
          label={t('terms.guarantee')}
          value={[
            t(`terms.guarantees.${guarantee.kind}`, { defaultValue: guarantee.kind }),
            guarantee.amount != null ? formatDealMoney(guarantee.amount) : null,
          ].filter(Boolean).join(' · ')}
        />
      )}
      {terms.term_months != null && (
        <TermLine label={t('terms.termMonths')} value={String(terms.term_months)} />
      )}
      {terms.iptu_paid_by && (
        <TermLine label={t('terms.iptuPaidBy')} value={t(`terms.payers.${terms.iptu_paid_by}`)} />
      )}
    </>
  );
}

/**
 * The conditions of a sale offer, each with the outcome still open.
 *
 * Settling one is its own action rather than an edit of the offer: `met`
 * on the last pending condition is what fires the win the acceptance
 * deliberately withheld, and `failed` kills the offer while leaving the
 * attendance open for whatever the client tries next.
 */
function ConditionList({ proposal, canSettle, onSettleCondition }: {
  proposal: DealProposal;
  canSettle: boolean;
  onSettleCondition: (proposalId: string, conditionId: string, state: 'met' | 'failed') => void;
}) {
  const { t } = useTranslation('features/deals');
  const conditions = proposal.sale_terms?.conditions ?? [];
  if (conditions.length === 0) return null;

  return (
    <div data-testid="proposal-conditions" className="flex flex-col gap-(--space-1)">
      {conditions.map((condition) => (
        <div key={condition.condition_id} className="flex flex-wrap items-center gap-(--space-2)">
          <Text
            component="span"
            data-testid="proposal-condition"
            className={cn(
              'rounded-full border px-(--space-2) py-(--space-0-5)',
              'text-[length:var(--text-tag)]',
              CONDITION_CLASS[condition.state],
            )}
          >
            {condition.description} · {t(`terms.conditionStates.${condition.state}`)}
          </Text>
          {canSettle && condition.state === 'pending' && (['met', 'failed'] as const).map((state) => (
            <Button
              key={state}
              variant="ghost-text"
              size="micro"
              onClick={() => onSettleCondition(proposal.proposal_id, condition.condition_id, state)}
              data-testid={`condition-${state}-button`}
            >
              {t(`terms.settle.${state}`)}
            </Button>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * What a sale offer says beyond its number.
 */
function SaleSummary({ proposal }: { proposal: DealProposal }) {
  const { t } = useTranslation('features/deals');
  const terms = proposal.sale_terms;
  if (!terms) return null;

  return (
    <>
      {terms.payment_method && (
        <TermLine
          label={t('terms.paymentMethod')}
          value={[
            t(`terms.payments.${terms.payment_method}`),
            terms.bank,
          ].filter(Boolean).join(' · ')}
        />
      )}
    </>
  );
}

/**
 * Everything about one offer that does not fit on its row.
 *
 * Terms and conditions live under the row rather than beside it because
 * they are what the two sides are arguing about: an offer shown as a
 * bare number reads as settled when it is not.
 *
 * @param props - The offer, whether it can still be acted on, and the
 *   callback that settles a condition.
 * @returns The terms and conditions block, or nothing when the offer
 *   carries neither.
 */
export function ProposalDetails({ proposal, canSettle, onSettleCondition }: {
  proposal: DealProposal;
  canSettle: boolean;
  onSettleCondition: (proposalId: string, conditionId: string, state: 'met' | 'failed') => void;
}) {
  const { t } = useTranslation('features/deals');

  return (
    <div className="flex flex-col gap-(--space-1) pl-(--space-1)">
      <RentalSummary proposal={proposal} />
      <SaleSummary proposal={proposal} />
      <ConditionList
        proposal={proposal}
        canSettle={canSettle}
        onSettleCondition={onSettleCondition}
      />
      {proposal.valid_until && (
        <TermLine
          label={t(isExpired(proposal) ? 'terms.expiredOn' : 'terms.validUntil')}
          value={formatLocalizedDate(proposal.valid_until)}
        />
      )}
    </div>
  );
}

export default ProposalDetails;
